/**
 * Upgrades the filter/sort form rendered by snippets/facets.liquid.
 *
 * The form works on its own as a plain GET submit. This script intercepts
 * changes, fetches the same URL, and swaps in the new facets + product grid so
 * the page does not reload. If any step fails it falls back to submitting the
 * form the normal way.
 */
class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.debounceTimer = null;

    this.form.addEventListener('change', (event) => {
      // Price inputs fire on every keystroke; give them a moment to settle.
      const isPriceInput = event.target.type === 'number';
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.apply(), isPriceInput ? 500 : 0);
    });

    this.form.addEventListener('input', (event) => {
      if (event.target.type !== 'number') return;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.apply(), 500);
    });

    // Keep the browser back button meaningful.
    window.addEventListener('popstate', () => this.load(window.location.href, false));
  }

  buildUrl() {
    const data = new FormData(this.form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      if (value === '') continue;
      params.append(key, value);
    }

    const query = params.toString();
    return query ? `${this.form.action}?${query}` : this.form.action;
  }

  apply() {
    const url = this.buildUrl();
    this.load(url, true);
  }

  async load(url, pushState) {
    this.setAttribute('data-loading', '');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      this.replace(doc, 'facet-filters');
      this.replace(doc, '[data-product-grid]');
      this.replace(doc, '[data-collection-pagination]');

      if (pushState) window.history.pushState({}, '', url);
      window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - 100 });
    } catch (error) {
      // Anything unexpected: let the browser do a normal form submit.
      this.form.submit();
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  replace(doc, selector) {
    const incoming = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (!incoming || !current) return;
    current.innerHTML = incoming.innerHTML;
  }
}

if (!customElements.get('facet-filters')) {
  customElements.define('facet-filters', FacetFilters);
}
